import lazySuspense from '@components/lazyWithSuspense';
import InterviewCodebook from '@pages/Interview/details/codebook';
import InterviewCustomAnalysis from '@pages/Interview/details/custom-analysis';
import InterviewOverallAnalysis from '@pages/Interview/details/overall-analysis';
import InterviewReport from '@pages/Interview/details/report';
import InterviewReportDetails from '@pages/Interview/details/report-details';
import InterviewResponseInsights from '@pages/Interview/details/response-insight';
import InterviewThemes from '@pages/Interview/details/themes';
import InterviewTranscripts from '@pages/Interview/details/transcripts';
import YourAnalyses from '@pages/Interview/details/your-analyses';
import VerifyLink from '@pages/OnBoarding/VerifyLink';
import CreateAccount from '@pages/Signup/CreateAccount';
import VerifyOtp from '@pages/Signup/VerifyOtp';
import AcceptShareRequestPage from '@pages/accept-share-request';
import Dashboard from '@pages/dashboard';
import SurveyOverallAnalysis from '@pages/dynamic-survey/details/overall-analysis';
import SurveyReport from '@pages/dynamic-survey/details/report';
import SurveyReportDetails from '@pages/dynamic-survey/details/report-details';
import ForgetPassword from '@pages/forget-password';
import ResetPassword from '@pages/reset-password';
import MicrosoftCallback from '@pages/auth/MicrosoftCallback';
import SignIn from '@pages/signin';
import VerifyEmailPage from '@pages/verify-email/VerifyEmailPage';
import WorkspaceAcceptInvitation from '@pages/workspaces/accept-invitation';
import WorkspaceActivation from '@pages/workspace-activation';
import { Navigate } from 'react-router-dom';
import Signup from '../pages/Signup/Welcome';
import OnboardingRoute from './OnboardingRoute';
import MfaSetupRoute from './MfaSetupRoute';
import RequireWorkspacePermission from '@components/workspace/RequireWorkspacePermission';
import { WORKSPACE_CAPABILITY } from '@constants/workspacePermissions';

const EmptyLayout = lazySuspense(() => import('./EmptyLayout'));
const PrivateRoute = lazySuspense(() => import('./PrivateRoute'));
const MfaSetupPage = lazySuspense(() => import('@pages/mfa-setup'));
const MyLayout = lazySuspense(() => import('@layouts/full/MyLayout'));
const EditAIParticipants = lazySuspense(() => import('@pages/ai-participants/edit'));
const ViewStudyAIParticipants = lazySuspense(() => import('@pages/ai-participants/study/[id]'));
const Billing = lazySuspense(() => import('@pages/billing'));

const WorkspaceDetailPage = lazySuspense(() => import('@pages/workspaces/detail'));
const WorkspaceNotAvailable = lazySuspense(() => import('@pages/workspaces/not-available'));
const DynamicSurveyPage = lazySuspense(() => import('@pages/dynamic-survey'));
const DynamicSurveyConfigure = lazySuspense(() => import('@pages/dynamic-survey/configure'));
const DynamicSurveyCreationPage = lazySuspense(() => import('@pages/dynamic-survey/create'));
const DynamicSurveyDetail = lazySuspense(() => import('@pages/dynamic-survey/details'));
const ParticipantData = lazySuspense(
  () => import('@pages/dynamic-survey/details/participant-data'),
);
const ParticipantDataInterview = lazySuspense(
  () => import('@pages/Interview/details/participant-data'),
);
const QuestionAnalysis = lazySuspense(
  () => import('@pages/dynamic-survey/details/question-analysis'),
);
const ResponseInsightPage = lazySuspense(
  () => import('@pages/dynamic-survey/details/response-insight'),
);
const DynamicSurveyEditPage = lazySuspense(() => import('@pages/dynamic-survey/edit'));
const DynamicSurveyShare = lazySuspense(() => import('@pages/dynamic-survey/share'));
const DynamicSurveyUpload = lazySuspense(() => import('@pages/dynamic-survey/upload'));
const Interview = lazySuspense(() => import('@pages/Interview'));
const CreateInterview = lazySuspense(() => import('@pages/Interview/create'));
const EditInterview = lazySuspense(() => import('@pages/Interview/edit'));
const InterviewDetails = lazySuspense(() => import('@pages/Interview/details'));
const InterviewUpload = lazySuspense(() => import('@pages/Interview/upload'));
const PaymentCancel = lazySuspense(() => import('@pages/payment-cancel'));
const PaymentSuccess = lazySuspense(() => import('@pages/payment-success'));
const TutorialDemos = lazySuspense(() => import('@pages/tutorial-demos'));
const HelpPage = lazySuspense(() => import('@pages/help'));
const GenerateAIParticipantsPage = lazySuspense(() => import('../pages/ai-participants/generate'));
const AIParticipantsPage = lazySuspense(() => import('../pages/ai-participants'));
const NotFound = lazySuspense(() => import('../pages/page-not-found'));
const UserProfile = lazySuspense(() => import('../pages/profile'));
const Steps = lazySuspense(() => import('../pages/OnBoarding/Steps/Step'));

export const Router = [
  { path: '/signin', element: <SignIn /> },
  { path: '/auth/microsoft/callback', element: <MicrosoftCallback /> },
  { path: '/signup', element: <Signup /> },
  { path: '/reset-password', element: <ResetPassword /> },
  { path: '/forget-password', element: <ForgetPassword /> },
  { path: '/Get-Started', element: <VerifyLink /> },
  { path: '/accept-share-request/:id/:studyType', element: <AcceptShareRequestPage /> },
  { path: '/workspaces/:workspaceId/accept', element: <WorkspaceAcceptInvitation /> },
  { path: '/verify-email', element: <VerifyEmailPage /> },
  { path: '/workspace-activation', element: <WorkspaceActivation /> },
  {
    path: '/mfa-setup',
    element: (
      <MfaSetupRoute>
        <MfaSetupPage />
      </MfaSetupRoute>
    ),
  },
  {
    path: '/workspaces/not-available',
    element: (
      <PrivateRoute>
        <EmptyLayout />
      </PrivateRoute>
    ),
    children: [{ path: '/workspaces/not-available', element: <WorkspaceNotAvailable /> }],
  },
  { path: 'signup/verify-otp', element: <VerifyOtp /> },
  { path: 'forget-password/send-otp', element: <VerifyOtp /> },
  { path: 'signup/create-account', element: <CreateAccount /> },
  {
    path: '/',
    element: <Navigate to="/dashboard" replace />,
  },
  {
    path: '/payment-success',
    element: (
      <PrivateRoute>
        <MyLayout hideSidebar showLogo hideHeader />
      </PrivateRoute>
    ),
    children: [{ path: '/payment-success', element: <PaymentSuccess /> }],
  },
  {
    path: '/onboarding',
    element: (
      <OnboardingRoute>
        <EmptyLayout />
      </OnboardingRoute>
    ),
    children: [{ path: '/onboarding', element: <Steps /> }],
  },
  {
    path: '',
    element: (
      <PrivateRoute>
        <MyLayout />
      </PrivateRoute>
    ),

    children: [
      { path: '/', element: <Navigate to="/dashboard" /> },
      { path: '/dashboard', element: <Dashboard /> },

      // Note:: this is the temporary comment will be enabled in future
      // {
      //   path: '/organizations',
      //   element: <OrganizationsListPage />,
      // },
      // {
      //   path: '/organizations/new',
      //   element: <NewOrganizationPage />,
      // },
      // {
      //   path: '/organizations/:id',
      //   element: <OrganizationDetailPage />,
      // },
      // {
      //   path: '/products',
      //   element: <ProductsListPage />,
      // },
      // {
      //   path: '/products/new',
      //   element: <NewProductPage />,
      // },
      // {
      //   path: '/products/:id',
      //   element: <ProductDetailPage />,
      // },
      { path: '/interviews', element: <Interview /> },
      { path: '/dynamic-surveys', element: <DynamicSurveyPage /> },
      { path: '/ai-participants', element: <AIParticipantsPage /> },

      { path: '/workspaces/:id', element: <WorkspaceDetailPage /> },
      { path: '/billing', element: <Billing /> },
      { path: '/payment-cancel', element: <PaymentCancel /> },
      { path: '/tutorial&demos', element: <TutorialDemos /> },
      { path: '/help', element: <HelpPage /> },
    ],
  },
  {
    path: '',
    element: (
      <PrivateRoute>
        <MyLayout isCollapsed />
      </PrivateRoute>
    ),
    children: [
      {
        path: '/dynamic-survey/create',
        element: (
          <RequireWorkspacePermission capability={WORKSPACE_CAPABILITY.CAN_EDIT}>
            <DynamicSurveyCreationPage />
          </RequireWorkspacePermission>
        ),
      },
      { path: '/dynamic-survey/configure', element: <DynamicSurveyConfigure /> },
      { path: '/dynamic-survey/share', element: <DynamicSurveyShare /> },
      { path: '/dynamic-survey/:id/edit', element: <DynamicSurveyEditPage /> },
      {
        path: '/dynamic-survey/:id',
        element: <DynamicSurveyDetail />,
        children: [
          { path: 'response-insight', element: <ResponseInsightPage /> },
          { path: 'question-analysis', element: <QuestionAnalysis /> },
          { path: 'report', element: <SurveyOverallAnalysis /> },
          { path: 'participant-data', element: <ParticipantData /> },
          {
            path: 'question-analysis/section/:sectionId/question/:questionId',
            element: <QuestionAnalysis />,
          },
          { path: 'custom-reports', element: <SurveyReport /> },
          { path: 'custom-reports/:reportId', element: <SurveyReportDetails /> },
        ],
      },
      {
        path: '/interview/create',
        element: (
          <RequireWorkspacePermission capability={WORKSPACE_CAPABILITY.CAN_EDIT}>
            <CreateInterview />
          </RequireWorkspacePermission>
        ),
      },
      { path: '/interview/edit/:id', element: <EditInterview /> },
      {
        path: '/interview/:id',
        element: <InterviewDetails />,
        children: [
          { path: 'response-insight', element: <InterviewResponseInsights /> },
          { path: 'participant-data', element: <ParticipantDataInterview /> },
          { path: 'transcripts', element: <InterviewTranscripts /> },
          { path: 'transcripts/:transcriptId', element: <InterviewTranscripts /> },
          { path: 'codebook', element: <InterviewCodebook /> },
          { path: 'themes', element: <InterviewThemes /> },
          { path: 'report', element: <InterviewOverallAnalysis /> },
          { path: 'custom-analysis', element: <InterviewCustomAnalysis /> },
          { path: 'analyses', element: <YourAnalyses /> },
          { path: 'custom-reports', element: <InterviewReport /> },
          { path: 'custom-reports/:reportId', element: <InterviewReportDetails /> },
        ],
      },
      {
        path: '/ai-participants/generate',
        element: (
          <RequireWorkspacePermission capability={WORKSPACE_CAPABILITY.CAN_EDIT}>
            <GenerateAIParticipantsPage />
          </RequireWorkspacePermission>
        ),
      },
      {
        path: '/ai-participants/generate/:studyId',
        element: (
          <RequireWorkspacePermission capability={WORKSPACE_CAPABILITY.CAN_EDIT}>
            <GenerateAIParticipantsPage />
          </RequireWorkspacePermission>
        ),
      },
    ],
  },
  {
    path: '',
    element: (
      <PrivateRoute>
        <MyLayout isCollapsed />
      </PrivateRoute>
    ),
    children: [
      {
        path: 'interview/upload',
        element: (
          <RequireWorkspacePermission capability={WORKSPACE_CAPABILITY.CAN_EDIT}>
            <InterviewUpload />
          </RequireWorkspacePermission>
        ),
      },
      {
        path: 'dynamic-survey/upload',
        element: (
          <RequireWorkspacePermission capability={WORKSPACE_CAPABILITY.CAN_EDIT}>
            <DynamicSurveyUpload />
          </RequireWorkspacePermission>
        ),
      },
      { path: 'user-profile', element: <UserProfile /> },
      { path: 'ai-participants/edit/:id', element: <EditAIParticipants /> },
      { path: 'ai-participants/study/:studyId', element: <ViewStudyAIParticipants /> },
    ],
  },
  { path: '*', element: <NotFound /> },
];
